import { FetchError, type RovoInstrumentedError } from './httpUtils';
import {
	hasRequestObservationSubscribers,
	publishRequestObservation,
	requestObservationDuration,
	requestObservationNow,
} from './request-observer';

export type GqlResponse<T> = GqlSuccessResponse<T> | GqlErrorResponse;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GqlVariables<T = Record<string, any>> = T;

export interface GqlSuccessResponse<T> {
	data: T;
	errors?: never;
}

interface GqlErrorResponse {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	data: any;
	errors?: GqlError[];
}

export type GqlError = {
	message: string;
	locations: {
		line: number;
		column: number;
	}[];
	extensions: {
		classification: string;
		errorSource: string;
		statusCode: number;
	};
};

const extractQueryName = (gqlQuery: string): string | undefined => {
	const operationMatch = gqlQuery.match(/(?:query|mutation)\s+(\w+)/);
	return operationMatch ? operationMatch[1] : undefined;
};

export const performGqlFetch = async <T, U>({
	gqlQuery,
	headerOptions,
	variables,
	abortController,
}: {
	gqlQuery: string;
	headerOptions?: Record<string, string>;
	variables?: GqlVariables<U>;
	abortController?: AbortController;
}): Promise<GqlSuccessResponse<T>> => {
	const startedAt = hasRequestObservationSubscribers() ? requestObservationNow() : undefined;
	const operation = startedAt === undefined ? undefined : extractQueryName(gqlQuery);
	try {
		const response = await fetch('/gateway/api/graphql', {
			headers: {
				'Content-Type': 'application/json',
				...headerOptions,
			},
			method: 'POST',
			body: JSON.stringify({ query: gqlQuery, variables }),
			signal: abortController?.signal,
		});

		const json: GqlResponse<T> = await response.json();

		if (json?.errors?.length) {
			await processGqlErrorResponse({ errors: json.errors, gqlQuery });
		}
		if (startedAt !== undefined) {
			publishRequestObservation({
				kind: 'graphql',
				method: 'POST',
				operation,
				durationMs: requestObservationDuration(startedAt),
				outcome: 'success',
				status: response.status,
			});
		}
		return json as GqlSuccessResponse<T>;
	} catch (err) {
		// GqlApiError is already processed
		if (err instanceof GqlApiError) {
			if (startedAt !== undefined) {
				publishRequestObservation({
					kind: 'graphql',
					method: 'POST',
					operation,
					durationMs: requestObservationDuration(startedAt),
					outcome: 'graphqlError',
				});
			}
			throw err;
		}

		// Handle abort signal gracefully, it shouldn't be considered an error despite being caught
		if (err instanceof Error && err.name === 'AbortError') {
			if (startedAt !== undefined) {
				publishRequestObservation({
					kind: 'graphql',
					method: 'POST',
					operation,
					durationMs: requestObservationDuration(startedAt),
					outcome: 'abort',
				});
			}
			return {
				// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- Existing GraphQL abort baseline: abort returns an empty typed success shape for generic callers.
				data: null as unknown as T,
				errors: undefined,
			};
		}
		const originalErrorMessage = err instanceof Error ? err.message : 'Unknown error';
		if (startedAt !== undefined) {
			publishRequestObservation({
				kind: 'graphql',
				method: 'POST',
				operation,
				durationMs: requestObservationDuration(startedAt),
				outcome: 'networkError',
			});
		}

		throw new FetchError(`GraphQL query failed (promise rejected): ${originalErrorMessage}`, {
			errorCode: 'performGqlFetchError',
		});
	}
};

type GraphqlApiError = {
	message: string;
	status_code: number;
	classification: string;
};

export class GqlApiError extends Error implements RovoInstrumentedError {
	message: string;
	errorAttributes: RovoInstrumentedError['errorAttributes'];

	constructor({ errors, queryName }: { errors: GraphqlApiError[]; queryName?: string }) {
		const errorsString = errors.map((error) => error.message).join(', ');
		const errorMessage = `GraphQL API error: ${errorsString}`;

		super(errorMessage);

		this.message = errorMessage;
		this.name = 'GraphQLError';
		this.errorAttributes = {
			errorCode: 'GqlApiError',
			queryName,
			rawErrorMessage: errorMessage,
			errors: errorsString,
		};
	}
}

const processGqlErrorResponse = async ({
	errors,
	gqlQuery,
}: {
	errors: GqlError[];
	gqlQuery: string;
}) => {
	const errorsToThrow: GraphqlApiError[] = [];

	errors.forEach((error) => {
		errorsToThrow.push({
			message: error.message,
			status_code: error.extensions.statusCode,
			classification: error.extensions.classification,
		});
	});

	throw new GqlApiError({
		errors: errorsToThrow,
		queryName: extractQueryName(gqlQuery),
	});
};
